import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Badge, // Accordion,
  // AccordionDetails,
  // AccordionSummary,
  Box,
  Divider,
  Grid,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';

import { Button } from '../../../../../components/buttons';
import VirtualizedList from '../../../../../components/list/VirtualizedList';
import { DayTimePicker } from '../../../../../components/timePicker';
import { APP_ENV } from '../../../../../configs';
import { IuseForm } from '../../../../../hooks/useForm';
import { generateRandomNumber } from '../../../../../utils/commonUtils';
import { TEXT_CONTENT } from '../../../../../utils/constants';
import { getDateDifference } from '../../../../../utils/date';
import { Shift } from '../../../contexts/ShiftProvider.types';
import { StyledFormError } from './ShiftController';

export default function ShiftTimeManagerUI({
  form,
  shiftList,
  shiftTypeName = 'APP',
  selectedDay,
  isLoading = false,
}: {
  form: IuseForm;
  shiftList: Shift[];
  shiftTypeName?: string;
  selectedDay?: string;
  isLoading?: boolean;
}) {
  const { errors, formData, setFieldValue, reset } = form;
  const onAddNewShiftClick = () => {
    const shiftLength = 8;
    const startDt = new Date(APP_ENV.SHIFT_BASE_DATE);
    const currDay = selectedDay ? Number(selectedDay) : 0;
    startDt.setDate(startDt.getDate() + currDay);
    startDt.setHours(7, 0, 0, 0);
    const endDt = new Date(startDt.toISOString());
    endDt.setHours(startDt.getHours() + shiftLength);
    const currId = generateRandomNumber();
    const newShift: IuseForm['formData'] = {
      id: currId,
      startDate: startDt.toISOString(),
      baseStartDate: startDt.toISOString(),
      endDate: endDt.toISOString(),
      shiftLength: shiftLength,
      hours: shiftLength,
      providerCount: 1,
      providerType: shiftTypeName,
    };

    setFieldValue(currId, { ...newShift, status: 'new' });
  };
  const onDeleteClick = (id: string) => {
    const newFormData = {
      ...formData,
      [id]: { ...(formData?.[id] || {}), status: 'delete' },
    };
    reset(newFormData);
  };
  const sortedShiftList = useMemo(() => {
    return shiftList.sort((a, b) => {
      if (a.status === 'new' && a.status === b.status) {
        return (
          new Date(a?.baseStartDate || a.startDate).valueOf() -
          new Date(b?.baseStartDate || b.startDate).valueOf()
        );
      } else if (a.status === 'new') return -1;
      else if (b.status === 'new') return 1;
      return (
        new Date(a?.baseStartDate || a.startDate).valueOf() -
        new Date(b?.baseStartDate || b.startDate).valueOf()
      );
    });
  }, [shiftList]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexFlow: 'column',
        gap: 1,
        px: 1,
        width: '100%',
        height: '100%',
        minWidth: '40rem',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontSize: '1.2rem', fontWeight: 750, my: 1 }}
        >
          {shiftTypeName}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          sx={{ height: '2.5rem' }}
          onClick={onAddNewShiftClick}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <AddIcon />
            {/* <Typography variant="subtitle1"> */}
            {TEXT_CONTENT.ADD_NEW_SHIFT}
            {/* </Typography> */}
          </Box>
        </Button>
      </Box>
      <Divider flexItem orientation="horizontal" sx={{ mx: -2, my: 0 }} />
      <Box
        sx={{
          // maxHeight: '20rem',
          // overflow: 'auto',
          my: -1,
          // py: 1,
          mr: -2,
          pr: 1,
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={9}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h5"
                  sx={{ fontSize: '1.12rem', fontWeight: 750, my: 1 }}
                >
                  {TEXT_CONTENT.SHIFT_START}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h5"
                  sx={{ fontSize: '1.12rem', fontWeight: 750, my: 1 }}
                >
                  {TEXT_CONTENT.SHIFT_END}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={2}>
            <Typography
              variant="h5"
              sx={{ fontSize: '1.12rem', fontWeight: 750, my: 1 }}
            >
              {TEXT_CONTENT.SHIFT_LENGTH}
            </Typography>
          </Grid>
          <Grid item xs={1}>
            <Box />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ height: 170, mr: -1 }}>
              {isLoading ? (
                <LoadingSkeleton />
              ) : Array.isArray(sortedShiftList) &&
                sortedShiftList.length > 0 ? (
                <VirtualizedList
                  width={'100%'}
                  height={170}
                  itemSize={55}
                  itemCount={sortedShiftList.length}
                  overscanCount={5}
                  style={{ scrollbarGutter: 'stable' }}
                >
                  {({ index, style }: { index: number; style: any }) => {
                    const each = sortedShiftList[index];
                    const isShiftExceedsMaximum =
                      Number(formData[each.id]?.shiftLength) >
                      APP_ENV.MAXIMUM_SHIFT_LENGTH_HR;
                    return (
                      <Box
                        key={each.id}
                        style={{
                          ...style,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Grid container spacing={2} sx={{ pt: 0 }}>
                          <Grid item xs={9}>
                            <Badge
                              color="info"
                              badgeContent={
                                each?.status === 'new'
                                  ? TEXT_CONTENT.NEW
                                  : each?.status === 'update'
                                    ? TEXT_CONTENT.EDIT
                                    : ''
                              }
                              anchorOrigin={{
                                horizontal: 'right',
                                vertical: 'top',
                              }}
                              sx={{
                                width: '100%',
                                '& .MuiBadge-badge': {
                                  fontSize: '0.75rem',
                                  letterSpacing: '1px',
                                  padding: '5px',
                                  fontWeight: 400,
                                  textTransform: 'uppercase',
                                },
                              }}
                              invisible={!['new'].includes(each?.status || '')}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 2,
                                  width: '100%',
                                }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  <DayTimePicker
                                    key="shift-start"
                                    value={formData[each.id]?.startDate}
                                    onChange={(v) => {
                                      const newFormField: Shift = {
                                        ...formData[each.id],
                                      };
                                      newFormField.startDate = v;
                                      const hrs = getDateDifference(
                                        newFormField.endDate,
                                        newFormField.startDate,
                                        'hour'
                                      );
                                      newFormField.shiftLength =
                                        hrs < 0 ? 168 - Math.abs(hrs) : hrs;
                                      setFieldValue(each.id, {
                                        ...newFormField,
                                        status: each?.status || 'update',
                                      });
                                    }}
                                  />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <DayTimePicker
                                    key="shift-end"
                                    value={formData[each.id]?.endDate}
                                    onChange={(v) => {
                                      const newFormField: Shift = {
                                        ...formData[each.id],
                                      };
                                      newFormField.endDate = v;
                                      const hrs = getDateDifference(
                                        newFormField.endDate,
                                        newFormField.startDate,
                                        'hour'
                                      );
                                      newFormField.shiftLength =
                                        hrs < 0 ? 168 - Math.abs(hrs) : hrs;
                                      setFieldValue(each.id, {
                                        ...newFormField,
                                        status: each?.status || 'update',
                                      });
                                    }}
                                    isReversed
                                  />
                                </Box>
                              </Box>
                            </Badge>
                          </Grid>
                          <Grid item xs={2}>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'start',
                                alignItems: 'center',
                                width: '100%',
                                height: '100%',
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="h5"
                                sx={[
                                  {
                                    fontSize: '1.1rem',
                                    fontWeight: 500,
                                    my: 1,
                                    textAlign: 'left',
                                    minWidth: '3.2rem',
                                  },
                                  isShiftExceedsMaximum && {
                                    color: 'warning.main',
                                  },
                                ]}
                              >
                                {`${formData[each.id]?.shiftLength || 0}hrs`}
                              </Typography>
                              {isShiftExceedsMaximum && (
                                <Tooltip
                                  sx={{ fontSize: '2rem' }}
                                  title={`Shift Length exceeds the maximum limit of ${APP_ENV.MAXIMUM_SHIFT_LENGTH_HR} hrs`}
                                >
                                  <WarningAmberIcon
                                    style={{ fontSize: '1.8rem' }}
                                    color="warning"
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </Grid>
                          <Grid item xs={1}>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                                height: '100%',
                              }}
                            >
                              <Tooltip
                                title={TEXT_CONTENT.DELETE}
                                sx={{ cursor: 'pointer' }}
                                onClick={() => onDeleteClick(each.id)}
                              >
                                <DeleteOutlineIcon
                                  sx={{ fontSize: '1.8rem' }}
                                  color="error"
                                />
                              </Tooltip>
                            </Box>
                          </Grid>
                          <StyledFormError>
                            {errors?.[each.id] || ''}
                          </StyledFormError>
                        </Grid>
                      </Box>
                    );
                  }}
                </VirtualizedList>
              ) : (
                <Grid item xs={12}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontSize: '1.12rem',
                      fontWeight: 750,
                      textAlign: 'center',
                    }}
                  >
                    {TEXT_CONTENT.NO_DATA_AVAILABLE}
                  </Typography>
                </Grid>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

const LoadingSkeleton = () => {
  return (
    <Grid container spacing={2} sx={{ pt: 0 }}>
      <Grid item xs={9}>
        <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
          <Box sx={{ flex: 1 }}>
            <Skeleton width="70%" />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Skeleton width="70%" />
          </Box>
        </Box>
      </Grid>
      <Grid item xs={2}>
        <Skeleton width="50%" />
      </Grid>
    </Grid>
  );
};
